import { useEffect, useState } from "react";

import { getApp, getApps, initializeApp } from "firebase/app";
import type { Messaging } from "firebase/messaging";
import { getMessaging, getToken, onMessage } from "firebase/messaging";

import { useConfig } from "@jti/app-context/useConfig";
import type { FirebaseSdkCredentials, PushNotificationData } from "@jti/fe-common";
import { isServerSide } from "@jti/fe-common";

import { isFirebaseMessagingConfigValid } from "./firebaseConfig";

type UseFirebaseNotificationsProps = {
  onPermissionGranted?: () => Promise<void> | void;
  onSuccess?: (token: string) => Promise<void> | void;
  onFail?: () => void;
  onDecline?: () => Promise<void> | void;
  onDismiss?: () => Promise<void> | void;
  onMessageReceived?: (reference: string) => void;
  onMessageClicked?: (reference: string) => void;
};

let messagingInstance: Messaging | null = null;
let isOnMessageRegistered = false;

const isNotificationAvailable = (): boolean => !isServerSide() && "Notification" in window;

const getIsEnvReady = () => !isServerSide() && "navigator" in window && isNotificationAvailable();

const getNotificationRedirectUrl = (redirectUrl?: string): string | undefined => {
  if (!redirectUrl) {
    return undefined;
  }

  try {
    const url = new URL(redirectUrl, window.location.origin);

    if (url.protocol === "https:") {
      return url.href;
    }
  } catch (error) {
    console.error("Invalid push notification redirect_url", error);

    return undefined;
  }

  console.error("Unsupported push notification redirect_url protocol");

  return undefined;
};

const openNotificationTarget = (redirectUrl?: string) => {
  if (redirectUrl) {
    window.location.href = redirectUrl;

    return;
  }

  window.focus();
};

const getFirebaseMessaging = (firebaseConfig: FirebaseSdkCredentials, messagingKey?: string): Messaging => {
  if (!getIsEnvReady()) {
    throw new Error("Firebase cannot be initiated on this environment");
  }

  if (!isFirebaseMessagingConfigValid(firebaseConfig, messagingKey)) {
    throw new Error("Firebase config is invalid");
  }

  if (!messagingInstance) {
    const app =
      getApps().length > 0
        ? getApp()
        : initializeApp({
            appId: firebaseConfig.firebaseAppId,
            apiKey: firebaseConfig.firebaseApiKey,
            authDomain: firebaseConfig.firebaseAuthDomain,
            messagingSenderId: firebaseConfig.firebaseMessagingSenderId,
            projectId: firebaseConfig.firebaseProjectId,
            storageBucket: firebaseConfig.firebaseStorageBucket,
          });

    messagingInstance = getMessaging(app);
  }

  return messagingInstance;
};

const registerOnMessageHandler = (
  messaging: Messaging,
  onMessageReceived?: (reference: string) => void,
  onMessageClicked?: (reference: string) => void
) => {
  if (isOnMessageRegistered) {
    return;
  }

  onMessage(messaging, (payload) => {
    const data: PushNotificationData | undefined = payload?.data;
    const notificationTitle = payload?.notification?.title ?? data?.title ?? "Notification title";
    const notificationOptions = {
      body: payload?.notification?.body ?? data?.body ?? "",
      icon: payload?.notification?.image ?? data?.image,
    };

    const notification = new Notification(notificationTitle, notificationOptions);

    const redirectUrl = getNotificationRedirectUrl(data?.redirect_url);
    const reference = data?.reference;
    if (reference) {
      onMessageReceived?.(reference);
    }

    notification.onclick = () => {
      if (reference) {
        onMessageClicked?.(reference);
      }

      openNotificationTarget(redirectUrl);
    };
  });

  isOnMessageRegistered = true;
};

export const useFirebaseNotifications = ({
  onPermissionGranted,
  onSuccess,
  onFail,
  onDecline,
  onDismiss,
  onMessageReceived,
  onMessageClicked,
}: UseFirebaseNotificationsProps = {}) => {
  const config = useConfig();
  const [token, setToken] = useState<string>();
  const [status, setStatus] = useState<NotificationPermission | "error" | "unsupported">(
    isNotificationAvailable() ? Notification.permission : "unsupported"
  );

  const requestPermissionAndGetToken = async (): Promise<NotificationPermission | "error" | "unsupported"> => {
    if (!getIsEnvReady()) {
      setStatus("unsupported");
      return "unsupported";
    }

    let permission: NotificationPermission;
    try {
      permission = await Notification.requestPermission();
    } catch (error) {
      console.error(error);
      setStatus("error");
      onFail?.();
      return "error";
    }

    if (permission === "default") {
      setStatus("default");
      await onDismiss?.();
      return "default";
    }

    if (permission === "denied") {
      setStatus("denied");
      try {
        await onDecline?.();
      } catch (error) {
        console.error(error);
        onFail?.();
      }
      return "denied";
    }

    try {
      if (!config.firebaseSdkCredentials) {
        setStatus("error");
        onFail?.();
        return "error";
      }

      await onPermissionGranted?.();

      const messaging = getFirebaseMessaging(config.firebaseSdkCredentials, config.firebaseVapidKey);

      const currentToken = await getToken(messaging, {
        vapidKey: config.firebaseVapidKey,
      });

      if (currentToken) {
        setToken(currentToken);
        registerOnMessageHandler(messaging, onMessageReceived, onMessageClicked);
        await onSuccess?.(currentToken);
        setStatus("granted");
        return "granted";
      } else {
        setStatus("error");
        onFail?.();
        return "error";
      }
    } catch (error) {
      console.error(error);
      setStatus("error");
      onFail?.();
      return "error";
    }
  };

  useEffect(() => {
    if (!getIsEnvReady() || Notification.permission !== "granted") {
      return;
    }

    try {
      if (!config.firebaseSdkCredentials) {
        return;
      }

      const messaging = getFirebaseMessaging(config.firebaseSdkCredentials, config.firebaseVapidKey);

      registerOnMessageHandler(messaging, onMessageReceived, onMessageClicked);
    } catch (error) {
      console.warn("Could not register onMessage handler", error);
    }
  }, [config, onMessageClicked, onMessageReceived]);

  return { token, status, requestPermissionAndGetToken };
};
